/**
 * Paces the tiled upload to the GPU. A band's write returns on the main thread long
 * before the GPU has consumed it, and the bytes wait in memory shared with the GPU
 * process until it does. Under SwiftShader the GPU of a 3D scene runs about a second
 * behind, so a budget of main-thread time alone would put the whole texture in that
 * memory within a few frames, and a later call would block until the GPU freed some.
 * So at most a window of bands is on its way at once: each frame's bands sit behind a
 * fence and leave the window once the GPU has passed it.
 */
/** No pacing: every frame may write as many bands as its time budget allows. */
export const UNPACED = { allowance: () => Infinity, markIssued: () => { } };
/**
 * 8 MiB of 2 MiB bands on their way to the GPU at once. Under SwiftShader a write
 * blocked for 60 ms once about 14 MiB were outstanding, so this is half of that.
 */
const IN_FLIGHT_BANDS = 4;
export class GpuPacer {
    fence;
    /** Oldest first. Written by `markIssued`, trimmed by `allowance` as fences pass. */
    inFlight = [];
    constructor(fence) {
        this.fence = fence;
    }
    allowance() {
        this.dropPassed();
        const onTheirWay = this.inFlight.reduce((sum, frame) => sum + frame.bands, 0);
        return Math.max(0, IN_FLIGHT_BANDS - onTheirWay);
    }
    markIssued(bands) {
        this.inFlight.push({ fence: this.fence(), bands });
    }
    /** Frees every fence still on its way, as the canvas that issued them goes. */
    dispose() {
        this.release(this.inFlight.length);
    }
    /** The GPU runs commands in order, so a passed fence has passed every earlier one too. */
    dropPassed() {
        for (let index = this.inFlight.length - 1; index >= 0; index--) {
            if (!this.inFlight[index].fence.isDone())
                continue;
            this.release(index + 1);
            return;
        }
    }
    /** Frees the oldest `count` frames' fences and forgets them. */
    release(count) {
        for (const { fence } of this.inFlight.splice(0, count))
            fence.dispose();
    }
}
//# sourceMappingURL=gpuPacer.js.map